'use client';

import React, { useEffect, useMemo, useState } from 'react';

// Function Imports
import { areFacetsEqual } from '@/lib/functions/areFacetsEqual';
import { defaultStoreNumber } from '@/lib/constants/store';
import { getSearchResults } from '@/lib/functions/search/getSearchResults';
import { normalizeSearchQuery } from '@/lib/functions/search/normalizeSearchQuery';

// Component Imports
import Heading from '@/components/heading';
import Loader from '@/components/loader';
import SearchFilters from '@/components/search/searchFilters';
import SearchGrid from '@/components/search/searchGrid';
import SearchCategories from '@/components/search/searchCategories';
import CorrectionNotice from '@/components/search/searchCorrectionNotice';
import { PriceRangeValues, AggregationGroup } from '@/components/search/searchFilters';
import Sidebar from '@/components/sidebar';

// Type Imports
import type { NormalizedProduct } from '@/lib/types/products/normalizedProduct';
import type { SearchResultsResponse, SearchResultsSuccess } from '@/lib/types/search';

// Constant import
import { brandSettings } from '@/lib/data/site/partysupplies/brandSettings';

// Style import
import styles from './searchResults.module.scss';

interface SearchResultsProps {
  isSearchPage: boolean;
  originalQuery?: string;
  suggestedQuery?: string;
  query: string;
  force?: boolean;
  setQuery?: React.Dispatch<React.SetStateAction<string>>;
  searchKey?: string;
  facets: Record<string, string | PriceRangeValues>;
  orderBy: string;
  page: number;
  pageSize: number;
  serverResults?: SearchResultsSuccess;
  title: string;
  description?: string;
}

const applySearchResults = (
  data: SearchResultsSuccess,
  setters: {
    setProducts: React.Dispatch<React.SetStateAction<NormalizedProduct[]>>;
    setSearchPageCount: React.Dispatch<React.SetStateAction<number>>;
    setTotalProducts: React.Dispatch<React.SetStateAction<number>>;
    setAggs: React.Dispatch<React.SetStateAction<AggregationGroup[]>>;
  },
) => {
  setters.setProducts(data.products ?? []);
  setters.setSearchPageCount(data.totalPages ?? 1);
  setters.setTotalProducts(data.totalProducts ?? 0);
  setters.setAggs(data.aggs ?? []);
};

const SearchResults: React.FC<SearchResultsProps> = ({
  isSearchPage,
  originalQuery = '',
  suggestedQuery = '',
  query,
  force = false,
  setQuery,
  searchKey,
  facets,
  orderBy,
  page,
  pageSize,
  serverResults,
  title,
  description,
}) => {
  const store = defaultStoreNumber;

  const [products, setProducts] = useState<NormalizedProduct[]>(serverResults?.products ?? []);
  const [initialLoading, setInitialLoading] = useState(!serverResults);
  const [gridLoading, setGridLoading] = useState(false);
  const [searchPageCount, setSearchPageCount] = useState<number>(serverResults?.totalPages ?? 1);
  const [totalProducts, setTotalProducts] = useState<number>(serverResults?.totalProducts ?? 0);
  const [aggs, setAggs] = useState<AggregationGroup[]>(serverResults?.aggs ?? []);
  const [originalAggs, setOriginalAggs] = useState<AggregationGroup[]>(
    brandSettings.hasProductSearch ? [] : (serverResults?.aggs ?? []),
  );
  const [hasFetchedOriginalAggs, setHasFetchedOriginalAggs] = useState<boolean>(
    !brandSettings.hasProductSearch && Boolean(serverResults),
  );
  const [localFacets, setLocalFacets] = useState<Record<string, string | PriceRangeValues>>(facets);
  // Aggs from the current query's first result set; items at zero there stay hidden while
  // items that later drop to zero gray out, so the filter sidebar never grows (FEL-3754).
  const [initialAggs, setInitialAggs] = useState<AggregationGroup[]>(serverResults?.aggs ?? []);
  const [localQuery, setLocalQuery] = useState<string>(query);
  const [localOriginalQuery, setLocalOriginalQuery] = useState<string>(originalQuery);
  const [localSuggestedQuery, setLocalSuggestedQuery] = useState<string>(suggestedQuery);

  const isSearchResultsSuccess = (data: SearchResultsResponse): data is SearchResultsSuccess => {
    return 'products' in data && Array.isArray(data.products);
  };

  const usingServerResults = useMemo(() => {
    if (!serverResults) return false;
    return localQuery === query && areFacetsEqual(localFacets, facets);
  }, [serverResults, localQuery, query, localFacets, facets]);

  useEffect(() => {
    setLocalQuery(query);
    // A new query starts a new first-paint snapshot; the next result set recaptures it.
    setInitialAggs([]);
  }, [query]);

  useEffect(() => {
    setLocalFacets(facets);
  }, [facets]);

  // Hydrate from server on initial load and on URL-driven navigation (page, sort, etc.)
  useEffect(() => {
    if (!serverResults) return;

    applySearchResults(serverResults, {
      setProducts,
      setSearchPageCount,
      setTotalProducts,
      setAggs,
    });
    // Capture-once: only the query's first result set becomes the first-paint snapshot.
    setInitialAggs((current) => (current.length > 0 ? current : (serverResults.aggs ?? [])));
    setLocalOriginalQuery(originalQuery);
    setLocalSuggestedQuery(suggestedQuery);
    setInitialLoading(false);

    if (!brandSettings.hasProductSearch) {
      setOriginalAggs(serverResults.aggs ?? []);
      setHasFetchedOriginalAggs(true);
    }
  }, [serverResults, originalQuery, suggestedQuery, page, orderBy, pageSize]);

  // Baseline aggregations for filter UI only (not needed on catalog pages without filters)
  useEffect(() => {
    if (!brandSettings.hasProductSearch || hasFetchedOriginalAggs) return;

    const fetchOriginalAggs = async () => {
      const fallbackResult = await getSearchResults({
        userQuery: '',
        force: force,
        searchKey,
        facets: {},
        page: 1,
        pageSize,
        orderBy,
        storeNumber: store,
      });

      if (isSearchResultsSuccess(fallbackResult)) {
        setOriginalAggs(fallbackResult.aggs ?? []);
        setHasFetchedOriginalAggs(true);
      }
    };

    fetchOriginalAggs();
  }, [force, searchKey, pageSize, orderBy, hasFetchedOriginalAggs, store]);

  // Client fetch only when the user changes filters/query locally (store is fixed for Party Supplies)
  useEffect(() => {
    if (usingServerResults) return;
    if (!localQuery && !searchKey && Object.keys(localFacets).length === 0) return;

    setGridLoading(true);

    const sanitizedFacets: Record<string, string | PriceRangeValues> = {};

    for (const [key, value] of Object.entries(localFacets)) {
      if (typeof value === 'string') {
        sanitizedFacets[key] = value;
      } else if (
        key === 'price_range' &&
        typeof value === 'object' &&
        'min_price' in value &&
        'max_price' in value
      ) {
        sanitizedFacets[key] = value;
      }
    }

    getSearchResults({
      userQuery: localQuery,
      force: force || false,
      searchKey,
      facets: sanitizedFacets,
      page,
      pageSize,
      orderBy,
      storeNumber: store,
    })
      .then((data: SearchResultsResponse) => {
        if (isSearchResultsSuccess(data)) {
          applySearchResults(data, {
            setProducts,
            setSearchPageCount,
            setTotalProducts,
            setAggs,
          });
          // Capture-once: only the query's first result set becomes the first-paint snapshot.
          setInitialAggs((current) => (current.length > 0 ? current : (data.aggs ?? [])));

          const { original, corrected, didCorrect } = normalizeSearchQuery(localQuery, force);
          setLocalOriginalQuery(original);
          setLocalSuggestedQuery(didCorrect ? corrected : '');
        } else {
          console.error('Search failed:', data);
        }
      })
      .catch((error) => {
        console.error('Error fetching search results:', error);
      })
      .finally(() => {
        setInitialLoading(false);
        setGridLoading(false);
      });
  }, [localQuery, force, searchKey, localFacets, orderBy, page, pageSize, store, usingServerResults]);

  if (initialLoading) {
    return <Loader variant="2" />;
  }

  return (
    <div className={styles.wrapper}>
      {!isSearchPage && (
        brandSettings?.hasProductSearch ? (
          <div className={styles.filters}>
            <SearchFilters
              title={title}
              aggs={aggs}
              originalAggs={originalAggs}
              facets={localFacets}
              setFacets={setLocalFacets}
              query={localQuery}
              setQuery={setQuery ?? setLocalQuery}
              initialAggs={initialAggs}
            />
          </div>
        ) : (
          <div className={styles.sideBar}>
            <Sidebar />
          </div>
        )
      )}
      <div className={styles.results}>
        <Heading variant="h1" fontSize="large">{title}</Heading>
        {description && description.trim() !== '' && (
          <div className={styles.description}>
            <p>{description}</p>
          </div>
        )}
        {localOriginalQuery !== localSuggestedQuery && !force && (
          <CorrectionNotice original={localOriginalQuery} corrected={localSuggestedQuery} />
        )}
        {isSearchPage && !gridLoading && (
          <SearchCategories loading={false} aggs={aggs} />
        )}
        {gridLoading ? (
          <Loader variant="2" />
        ) : (
          <SearchGrid
            loading={false}
            searchPage={page}
            searchPageCount={searchPageCount}
            searchPageSize={pageSize}
            orderBy={orderBy}
            totalProducts={totalProducts}
            products={products}
          />
        )}
      </div>
    </div>
  );
};

export default SearchResults;
